/**
 * "N cards won't hold" — the red chip beside Closeout (Corbin's pick 2A,
 * 10/28). A visit whose card won't hold is a job we already booked, not a new
 * request, so its home is Close Out: a tap opens Close Out, where the red
 * "Card won't hold — call the customer" rows sit at the top with Send new card
 * link / Collect on site. These rows used to ride the booking chip (#2045);
 * they never count there now, so the bookings / New requests number means
 * new customers only.
 *
 * Reads the one list (GET /api/card-holds/alerts), the same one Close Out's
 * strip reads. Office only. Hidden at zero. Never the bell, no phone pings.
 */
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CreditCard } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PERMISSIONS } from "@/lib/permissions";
import { CARD_HOLD_ALERTS_KEY, type CardHoldAlert } from "@/components/card-hold/card-hold";

export function closeoutHoldLabel(n: number): string {
    return `${n} card${n === 1 ? "" : "s"} won't hold`;
}

export function CloseoutHoldChip() {
    const { can } = useAuth();
    const enabled = can(PERMISSIONS.jobs.schedule);
    const navigate = useNavigate();
    const q = useQuery({
        queryKey: CARD_HOLD_ALERTS_KEY,
        queryFn: () => api.get<{ alerts: CardHoldAlert[]; count: number }>("/api/card-holds/alerts"),
        enabled,
        refetchInterval: 60_000,
        refetchOnWindowFocus: true,
        staleTime: 30_000,
        retry: false,
    });
    const n = Array.isArray(q.data?.alerts) ? q.data.alerts.length : 0;
    if (!enabled || n === 0) return null;
    return (
        <button
            type="button"
            className="bkchip t-red"
            style={{ minWidth: 0 }}
            data-testid="closeout-hold-chip"
            aria-label={`${closeoutHoldLabel(n)}. Call the customer. Opens Close Out.`}
            onClick={() => navigate("/close-out")}
        >
            <CreditCard size={16} aria-hidden />
            <span className="c">{closeoutHoldLabel(n)}</span>
        </button>
    );
}
